<script setup>
import { can } from '@layouts/plugins/casl'

const props = defineProps({
  modelValue: Boolean,
  annotations: { type: Array, default: () => ([]) },
  currentPage: { type: Number, default: 1 },
  artworkSelect: { type: Number, default: 0 },
  lineItem: { type: Object, default: () => ({}) },
  readonly: { type: Boolean, default: false }
})

const emit = defineEmits(['update:modelValue', 'update:annotations', 'onSetHover', 'onComment'])

const isCurrentPageOnlyAnnotations = ref(true)

const filteredAnnotations = computed(() => {
  if (isCurrentPageOnlyAnnotations.value) {
    return props.annotations.filter(i => i.page === props.currentPage)
  } else {
    return props.annotations
  }
})

const showAnnotationMenu = (annotation) => {
  const selectedAnnotation = props.annotations.find(item => item.id === annotation.id)
  if (selectedAnnotation) {
    selectedAnnotation.isShownAnnotationMenu = true
  }
}

</script>

<template>
  <VNavigationDrawer
    :model-value="modelValue"
    permanent
    location="end"
    class="annotations-sidebar"
    @update:modelValue="emit('update:modelValue')"
  >
    <VBtn
      variant="elevated"
      density="compact"
      icon="tabler-x"
      color="light"
      class="rounded-sm"
      @click="emit('update:modelValue', false)"
    />
    <VContainer fluid>
      <VRow>
        <VCol class="px-0">
          <h4 class="mb-1">
            Latest Annotations
          </h4>
          <VSwitch
            v-model="isCurrentPageOnlyAnnotations"
            class="default-switch"
            inset
          >
            <template #label>
              Current page only
              <VTooltip
                class="default-tooltip"
                activator=".annotations-info-div"
                offset="5"
              >
                <span>Show annotations placed on the current page</span>
              </VTooltip>
              <div class="annotations-info-div">
                <VIcon
                  icon="tabler-info-circle"
                  size="26"
                />
              </div>
            </template>
          </VSwitch>
          <div v-if="!filteredAnnotations.length">
            <VAlert show>
              No Annotations
            </VAlert>
          </div>
          <div v-else>
            <div
              v-for="(annotation, key) in filteredAnnotations"
              :key="key"
              class="position-relative annotation-list-item"
              @mouseover="$emit('onSetHover', { annotation: annotation, status: true })"
              @mouseout="$emit('onSetHover', { annotation: annotation, status: false })"
            >
              <h4 class="text-primary mb-0 text-right">
                {{ annotation.index }}
              </h4>
              <p class="mb-2">
                {{ annotation.comment }}
              </p>
              <VRow>
                <VCol
                  md="10"
                  sm="8"
                >
                  <small>{{ annotation.username }} at {{ annotation.created_at }}</small><br>
                  <small
                    v-if="annotation.is_resolved"
                    class="text-success"
                  >
                    Resolved
                  </small>
                  <small
                    v-if="!annotation.is_resolved"
                    class="text-primary"
                  >
                    Not Resolved
                  </small>
                </VCol>
                <VCol
                  md="2"
                  sm="4"
                  class="d-flex flex-column justify-content-center align-items-center"
                >
                  <VBtn
                    v-if="
                      !annotation.is_resolved
                      && annotation.page === currentPage
                      && [0, 1].includes(artworkSelect)
                      && can('change', 'brandingartworkannot')
                      && lineItem?.is_active_campaign
                      && !readonly
                    "
                    variant="elevated"
                    density="compact"
                    icon="mdi-edit"
                    color="light"
                    class="rounded-sm"
                    @click="showAnnotationMenu(annotation)"
                  >
                    <VIcon icon="mdi-edit" />
                    <VTooltip
                      class="default-tooltip"
                      activator="parent"
                      location="left"
                      offset="5"
                    >
                      <span>Show Edit Box</span>
                    </VTooltip>
                  </VBtn>
                  <VBtn
                    v-if="
                      can('add', 'brandingartworkannotcomment')
                      && !annotation.is_resolved
                      && lineItem?.is_active_campaign
                      && !readonly
                    "
                    variant="elevated"
                    density="compact"
                    icon="mdi-chat"
                    color="light"
                    class="rounded-sm"
                    @click="$emit('onComment', annotation)"
                  >
                    <VIcon icon="mdi-chat" />
                    <VTooltip
                      class="default-tooltip"
                      activator="parent"
                      location="left"
                      offset="5"
                    >
                      <span>Comment</span>
                    </VTooltip>
                  </VBtn>
                </VCol>
              </VRow>
              <ul class="child-comments pl-3">
                <li
                  v-for="(comment, commentKey) in annotation.comments"
                  :key="commentKey"
                  class="child-comment"
                >
                  <div class="child-comment__text">
                    {{ comment.content }}
                  </div>
                  <small>{{ comment.username }} at {{ comment.created_at }}</small>
                </li>
              </ul>
              <h5 class="text-secondary annotation-page-label text-right">
                Page {{ annotation.page }}
              </h5>
            </div>
          </div>
        </VCol>
      </VRow>
    </VContainer>
  </VNavigationDrawer>
</template>

<style lang="scss">
.annotation-list-item {
  padding: 10px;
  &:hover {
    background-color: rgb(var(--v-theme-on-surface), 0.1);
  }
  &:not(:last-child) {
    border-bottom: 1px solid rgba(128, 128, 128, 0.2);
  }
}
.annotations-sidebar {
  z-index: 1200 !important;
}
</style>
